import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { outputFromObservable } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SpinnerService, UtilityService } from '@eforall/common';
import { Subject } from 'rxjs';
import { ButtonContainerWidget } from '../../../widgets/button-container';
import { DialogAction } from '../dialog-action';



@Component({
	selector: 'dashboard-dialog-frame',
	imports: [
		ButtonContainerWidget
	],
	templateUrl: './dialog-frame.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['dialog-frame.part.scss'],
})

export class DashboardDialogFramePart {

	//
	// Required inputs
	//
	public readonly header = input.required<string>();
	public readonly dialogRef = input.required<MatDialogRef<any, DialogAction<unknown>>>();

	//
	// Optional inputs
	//
	public readonly icon = input<string>('fa-info-circle');
	public readonly actions = input<DialogAction[]>([]);
	public readonly alertType = input<'info' | 'success' | 'warning' | 'error' | 'editor'>('info');
	public readonly closeX = input<boolean>(true);


	//
	// Event outputs
	//
	private readonly _actionClick$ = new Subject<DialogAction<unknown>>();
	public readonly actionClick = outputFromObservable(this._actionClick$);
	public readonly closed = output();

	//
	// Injected services
	//
	private router = inject(Router);
	private util = inject(UtilityService);
	public spinner = inject(SpinnerService);

	//
	// State signals
	//
	public readonly handlingClick = signal(false);

	constructor() {
		effect(() => {
			const dialogRef = this.dialogRef();
			const actions = this.actions();

			/**
			 * Dialogs must be closed with the top-right x or with an action button.
			 * The page spinner will also show on an open dialog, and we do not want
			 * the user to be able to press esc to close the dialog while the spinner
			 * is active (e.g. a func is being awaited).
			 */
			dialogRef.disableClose = actions.length > 0;
		});


	}


	async click(action: DialogAction<unknown>) {

		this.handlingClick.set(true);

		//
		// perform the action
		//
		if (action.linkType == 'routerLink') {
			this.router.navigate([action.url]);
		}
		else if (action.linkType == 'href') {
			//
			// Nothing to do. The a[href] will handle
			// the navigation
			//
		}
		else if (action.linkType == 'callback') {

			//
			// Detect if the callback function was not configured
			//
			if (!action.callback) this.util.log.errorMessage(`Action '${action.id}' with linkType 'callback' was clicked but there is not callback function defined.`);
			else action.callbackResult = (await action.callback()) ?? action.callbackResult;
		}
		else if (action.linkType == 'actionClick-event') {

			//
			// Detect if the actionClick event was not configured.
			//
			if (!this._actionClick$.observed) this.util.log.errorMessage(`Action '${action.id}' with linkType 'actionClick-event' was clicked but the (actionClick) EventEmitter has no registered observers.`);
			else this._actionClick$.next(action);
		}


		//
		// close the dialog
		//
		if (action.willCloseDialog) {
			this.closed.emit();
			this.dialogRef().close(action);
		}

		this.handlingClick.set(false);
	}


	public closeClick(): void {
		this.closed.emit();
		this.dialogRef().close();
	}
}

